import {
  LogsEvent,
  LogsInitConfiguration,
  datadogLogs,
} from '@datadog/browser-logs'
import {
  PropagatorType,
  RumEvent,
  RumInitConfiguration,
  datadogRum,
} from '@datadog/browser-rum'
import { config } from 'config'

interface ErrorFilter {
  name: string
  test: (message: string) => boolean
}

const ddApplicationID = process.env.REACT_APP_DD_RUM_APPLICATION_ID
const ddClientToken = process.env.REACT_APP_DD_RUM_CLIENT_TOKEN
const environment =
  process.env.REACT_APP_DD_RUM_ENVIRONMENT ||
  window?.location?.hostname ||
  process.env.NODE_ENV

const REACT_APP_API_URL = config.urls.api
const REACT_APP_ATLAS_URL = config.urls.atlas

export const logger = datadogLogs.createLogger('ddLogger', {
  handler: 'console',
})

const errorFilters: ErrorFilter[] = [
  {
    name: 'EmailLinkValidation',
    test: (message) =>
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(
        message,
      ),
  },
]

// Sampling posture, defined here and nowhere else (sc-260370).
// RUM stays at 10% of sessions: with ~228 resources per view on legacy pages,
// resource/long-task tracking multiplies event volume, so any change here
// needs a cost check first. Use RUM retention filters rather than raising this.
// Logs keep every session so error logs are never dropped.
export const RUM_SESSION_SAMPLE_RATE = 10
export const LOGS_SESSION_SAMPLE_RATE = 100

// Only the `datadog` propagator: the default also adds `traceparent` /
// `tracestate`, which the Atlas and API nginx CORS configs do not allow, so
// every traced request would fail its preflight.
const TRACE_PROPAGATORS: PropagatorType[] = ['datadog']

// The web nginx sends a Report-Only "ideal" CSP whose violations are already
// aggregated in report-uri.com, and nothing is actually blocked. SDK v7 turns
// every CSP report into a RUM error, and RUM's own intake isn't in that
// policy, so each RUM batch triggers a new violation that becomes the next
// error: a feedback loop of hundreds of errors per session that buries the
// real ones. Enforced violations (something really blocked) are still kept.
const isReportOnlyCspViolation = (event: RumEvent) =>
  event.type === 'error' && event.error.csp?.disposition === 'report'

type DatadogEnv = {
  applicationId: string
  clientToken: string
  environment: string
  apiUrl?: string
  atlasUrl?: string
}

// Matched as URL prefixes against the full request URL. The trailing slash
// keeps e.g. `https://atlas-main...com.evil.io` from matching.
// WHY origins and not the old API regex: SDK v3 tested that regex against the
// request *origin*, where `https://.*/` could never match (so the API was not
// traced at all). v5+ tests the full URL, where the same regex would match
// every https request, third parties included.
const getAllowedTracingUrls = ({
  apiUrl,
  atlasUrl,
}: DatadogEnv): RumInitConfiguration['allowedTracingUrls'] =>
  [atlasUrl, apiUrl]
    .filter((url): url is string => url !== undefined)
    .map((url) => ({
      match: `${new URL(url).origin}/`,
      propagatorTypes: TRACE_PROPAGATORS,
    }))

const getSharedConfiguration = ({ clientToken, environment }: DatadogEnv) => ({
  clientToken,
  site: 'datadoghq.com' as const,
  env: environment,
  service: 'joor-react',
  silentMultipleInit: true,
  trackSessionAcrossSubdomains: true,
  version: process.env.REACT_APP_VERSION,
})

export const getRumConfiguration = (env: DatadogEnv): RumInitConfiguration => ({
  ...getSharedConfiguration(env),
  applicationId: env.applicationId,
  sessionSampleRate: RUM_SESSION_SAMPLE_RATE,
  sessionReplaySampleRate: 0,
  // Already the SDK defaults, stated explicitly because they drive event
  // volume and are what makes LCP/INP/resources diagnosable.
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
  // Replaces the old beforeSend that copied full response headers and bodies
  // into every event (sc-260370). `true` records only the SDK's small
  // allowlist of cache/perf headers (cache-control, etag, content-type,
  // server-timing, ...) on resource events, and the SDK always drops
  // sensitive ones (token, cookie, authorization, ...). The status code is
  // already on every resource event as @resource.status_code.
  trackResourceHeaders: true,
  allowedTracingUrls: getAllowedTracingUrls(env),
  // Defaults to true in v7 and adds a `baggage` header to traced requests,
  // which the Atlas/API nginx CORS configs reject (preflight fails, Atlas
  // calls never reach the server). Same reason as TRACE_PROPAGATORS above.
  propagateTraceBaggage: false,
  beforeSend: (event) => !isReportOnlyCspViolation(event),
})

export const getLogsConfiguration = (
  env: DatadogEnv,
): LogsInitConfiguration => ({
  ...getSharedConfiguration(env),
  sessionSampleRate: LOGS_SESSION_SAMPLE_RATE,
  forwardErrorsToLogs: true,
  forwardConsoleLogs: ['error'],
  // Filter out error that might be caused by link validation/ pre-fetch rendering from email clicks
  // https://github.com/DataDog/browser-sdk/issues/2715
  beforeSend: (event: LogsEvent) => {
    if (event?.message && typeof event.message === 'string') {
      const msg = event.message
      for (const filter of errorFilters) {
        if (filter.test(msg)) {
          return false
        }
      }
    }
    return true
  },
})

const initializeDatadogRUM = () => {
  if (environment && ddApplicationID && ddClientToken) {
    const env = {
      applicationId: ddApplicationID,
      clientToken: ddClientToken,
      environment,
      apiUrl: REACT_APP_API_URL,
      atlasUrl: REACT_APP_ATLAS_URL,
    }
    datadogRum.init(getRumConfiguration(env))
    datadogLogs.init(getLogsConfiguration(env))
    if (process.env.NODE_ENV !== 'development') {
      logger.setHandler('http')
    }
  }
}

export const setLocationForDatadogRUM = (location: any) => {
  datadogRum.setGlobalContextProperty('location', location)
}

export const setUserForDatadog = ({
  userId,
  accountId,
}: {
  userId: number | null
  accountId: number | null
}) => {
  if (environment && ddApplicationID && ddClientToken && userId && accountId) {
    // First-class user identity: populates the RUM "User" panel and lets
    // sessions be filtered by usr.id (the global context below does not).
    datadogRum.setUser({
      id: String(userId),
      accountId: accountId,
    })
    datadogRum.setGlobalContextProperty('user', {
      userId: userId,
      accountId: accountId,
    })
    datadogLogs.setGlobalContextProperty('user', {
      userId: userId,
      accountId: accountId,
    })
  }
}

export const reportRenderErrorToDatadog = (
  error: Error,
  info?: { componentStack?: string },
) => {
  datadogRum.addError(error, {
    source: 'error-boundary',
    componentStack: info?.componentStack,
  })
}

export const logMutationError = (
  mutationName: string,
  variables: Record<string, unknown>,
  errorMsg: string,
): void => {
  if (!environment || !ddApplicationID || !ddClientToken) {
    return
  }

  datadogLogs.setGlobalContextProperty('mutation', {
    mutationName,
    variables,
  })
  console.error(errorMsg)
  datadogLogs.removeGlobalContextProperty('mutation')
}

export default initializeDatadogRUM
